import { useEffect, useState } from 'react';
import { Download, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '../services/supabase';
import type { Database } from '../types/database.types';
import { leerVarias, listaEnCastellano } from '../lib/leerVarias';
import {
  leerContabilidadPorConcepto,
  csvDeContabilidad,
  mesesConVentas,
  etiquetaDeMes,
  filasDelMes,
  nombreDelCsv,
  TODOS_LOS_MESES,
  type FilaDeContabilidad,
} from '../services/contabilidadPorConcepto';

// Los nombres de lo que se lee, para poder contar CUÁL no se pudo leer.
const QUE_SESIONES = 'las sesiones';
const QUE_NOTAS = 'las valoraciones';
const QUE_GRUPOS = 'los grupos';
const QUE_ENTRENADORES = 'los entrenadores';
const QUE_ESPECIALES = 'las clases especiales';

type UserCreditRow = Database['public']['Tables']['user_credits']['Row'];
type SessionRow = Database['public']['Tables']['sessions']['Row'];
type GroupRow = Database['public']['Tables']['groups']['Row'];
type SessionRatingRow = Database['public']['Tables']['session_ratings']['Row'];

// loadCompras: el `.select(..., profiles(full_name), session_packs(name, price_cents))`
// devuelve este shape compuesto. No usamos `Pick<UserCreditRow, ...>` porque ya se
// pide casi todo el row; lo declaramos derivado del Row + los embeds.
type CompraRow = Pick<
  UserCreditRow,
  'id' | 'purchase_date' | 'sessions_total' | 'sessions_remaining' | 'credit_type'
> & {
  profiles: { full_name: string } | null;
  session_packs: { name: string; price_cents: number } | null;
};

// loadSesiones: las consultas auxiliares (`groups`, `special_classes`, ratings)
// devuelven subconjuntos planos sin embeds — basta con `Pick<>` del Row.
type SessionListRow = Pick<
  SessionRow,
  | 'id'
  | 'date'
  | 'time_start'
  | 'current_count'
  | 'status'
  | 'is_special_class'
  | 'group_id'
  | 'special_class_id'
>;
type GroupListRow = Pick<GroupRow, 'id' | 'name' | 'class_type' | 'monitor_id'>;
type GroupWithMonitor = GroupListRow & { monitor_name: string };
type MonitorListRow = { id: string; full_name: string };
type SpecialClassListRow = { id: string; name: string };
type RatingListRow = Pick<SessionRatingRow, 'session_id' | 'rating'>;

interface CompraAlumno {
  id: string;
  purchase_date: string;
  sessions_total: number;
  sessions_remaining: number;
  alumno: string;
  bono: string;
  precio: number;
  tipo: string;
}

interface SesionEntrenador {
  id: string;
  date: string;
  time_start: string;
  monitor: string;
  grupo: string;
  tipo: string;
  alumnos: number;
  status: string;
  nota_media: number | null;
}

const TIPO_COLORS: Record<string, string> = {
  grupal: 'badge-gold',
  individual: 'badge-silver',
  especial: 'badge-warning',
};
const TIPO_LABELS: Record<string, string> = {
  grupal: 'Grupal',
  individual: 'Individual',
  especial: 'Especial',
};

// ── Sub-componente: vista por alumno ──────────────────────────────────────────

function VistaAlumno({ compras, mes }: { compras: CompraAlumno[]; mes: string }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (name: string) =>
    setExpanded((s) => {
      const n = new Set(s);
      n.has(name) ? n.delete(name) : n.add(name);
      return n;
    });

  const byAlumno: Record<string, CompraAlumno[]> = {};
  compras.forEach((c) => {
    if (!byAlumno[c.alumno]) byAlumno[c.alumno] = [];
    byAlumno[c.alumno].push(c);
  });
  const entries = Object.entries(byAlumno).sort((a, b) => a[0].localeCompare(b[0]));

  function exportCSV() {
    const lines = ['Alumno,Bono,Tipo,Fecha,Precio (€)'];
    entries.forEach(([alumno, cs]) => {
      cs.forEach((c) => {
        lines.push(
          `"${alumno}","${c.bono}","${TIPO_LABELS[c.tipo] ?? c.tipo}","${format(new Date(c.purchase_date), 'dd/MM/yyyy')}","${(c.precio / 100).toFixed(2)}"`
        );
      });
    });
    dl(lines.join('\n'), `contabilidad_alumnos_${mes}.csv`);
  }

  const totalMes = compras.reduce((s, c) => s + c.precio, 0);

  return (
    <>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-text-secondary text-sm">
          {compras.length} compras · {entries.length} alumnos ·{' '}
          <span className="text-gold-primary font-oswald">{(totalMes / 100).toFixed(2)}€</span>
        </div>
        <button onClick={exportCSV} className="btn-gold flex items-center gap-2">
          <Download size={16} /> Exportar CSV
        </button>
      </div>
      <div className="card p-0 overflow-hidden">
        {entries.length === 0 ? (
          <p className="text-text-secondary text-center py-12 text-sm">Sin compras en este mes</p>
        ) : (
          <table className="w-full">
            <thead className="border-b border-bg-border bg-bg-elevated">
              <tr>
                <th className="table-th w-8" />
                <th className="table-th">Alumno / Bono</th>
                <th className="table-th">Tipo</th>
                <th className="table-th">Fecha</th>
                <th className="table-th text-right">Precio</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(([alumno, cs]) => {
                const total = cs.reduce((s, c) => s + c.precio, 0);
                const open = expanded.has(alumno);
                return (
                  <>
                    <tr
                      key={`h-${alumno}`}
                      className="cursor-pointer hover:bg-bg-elevated transition-colors border-b border-bg-border bg-bg-elevated/50"
                      onClick={() => toggle(alumno)}
                    >
                      <td className="table-td w-8">
                        {open ? (
                          <ChevronDown size={14} className="text-gold-primary" />
                        ) : (
                          <ChevronRight size={14} className="text-text-secondary" />
                        )}
                      </td>
                      <td className="table-td">
                        <span className="font-medium text-white">{alumno}</span>
                        <span className="text-text-secondary text-xs ml-2">
                          {cs.length} bono{cs.length > 1 ? 's' : ''}
                        </span>
                      </td>
                      <td className="table-td" />
                      <td className="table-td" />
                      <td className="table-td text-right font-oswald text-gold-primary">
                        {(total / 100).toFixed(2)}€
                      </td>
                    </tr>
                    {open &&
                      cs.map((c) => (
                        <tr key={c.id} className="table-row border-b border-bg-border/50">
                          <td className="table-td" />
                          <td className="table-td pl-8 text-text-secondary text-sm">{c.bono}</td>
                          <td className="table-td">
                            <span className={TIPO_COLORS[c.tipo] ?? 'badge-gold'}>
                              {TIPO_LABELS[c.tipo] ?? c.tipo}
                            </span>
                          </td>
                          <td className="table-td text-text-secondary text-xs">
                            {format(new Date(c.purchase_date), 'dd/MM/yyyy')}
                          </td>
                          <td className="table-td text-right text-text-secondary font-inter">
                            {(c.precio / 100).toFixed(2)}€
                          </td>
                        </tr>
                      ))}
                  </>
                );
              })}
            </tbody>
            <tfoot className="border-t-2 border-bg-border bg-bg-elevated">
              <tr>
                <td
                  colSpan={4}
                  className="table-td text-right font-oswald text-text-secondary uppercase tracking-wider text-xs"
                >
                  Total mes
                </td>
                <td className="table-td text-right font-oswald text-gold-primary text-lg">
                  {(totalMes / 100).toFixed(2)}€
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </>
  );
}

// ── Sub-componente: vista por tipo de bono ────────────────────────────────────

function VistaBono({ compras, mes }: { compras: CompraAlumno[]; mes: string }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (name: string) =>
    setExpanded((s) => {
      const n = new Set(s);
      n.has(name) ? n.delete(name) : n.add(name);
      return n;
    });

  const byBono: Record<string, CompraAlumno[]> = {};
  compras.forEach((c) => {
    if (!byBono[c.bono]) byBono[c.bono] = [];
    byBono[c.bono].push(c);
  });
  const entries = Object.entries(byBono).sort(
    (a, b) => b[1].reduce((s, c) => s + c.precio, 0) - a[1].reduce((s, c) => s + c.precio, 0)
  );

  function exportCSV() {
    const lines = ['Bono,Alumno,Tipo,Fecha,Precio (€)'];
    entries.forEach(([bono, cs]) => {
      cs.forEach((c) => {
        lines.push(
          `"${bono}","${c.alumno}","${TIPO_LABELS[c.tipo] ?? c.tipo}","${format(new Date(c.purchase_date), 'dd/MM/yyyy')}","${(c.precio / 100).toFixed(2)}"`
        );
      });
    });
    dl(lines.join('\n'), `contabilidad_bonos_${mes}.csv`);
  }

  const totalMes = compras.reduce((s, c) => s + c.precio, 0);

  return (
    <>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-text-secondary text-sm">
          {entries.length} tipo{entries.length > 1 ? 's' : ''} de bono · {compras.length} ventas ·{' '}
          <span className="text-gold-primary font-oswald">{(totalMes / 100).toFixed(2)}€</span>
        </div>
        <button onClick={exportCSV} className="btn-gold flex items-center gap-2">
          <Download size={16} /> Exportar CSV
        </button>
      </div>
      <div className="card p-0 overflow-hidden">
        {entries.length === 0 ? (
          <p className="text-text-secondary text-center py-12 text-sm">Sin compras en este mes</p>
        ) : (
          <table className="w-full">
            <thead className="border-b border-bg-border bg-bg-elevated">
              <tr>
                <th className="table-th w-8" />
                <th className="table-th">Bono / Alumno</th>
                <th className="table-th">Tipo</th>
                <th className="table-th">Fecha</th>
                <th className="table-th text-right">Precio</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(([bono, cs]) => {
                const total = cs.reduce((s, c) => s + c.precio, 0);
                const open = expanded.has(bono);
                return (
                  <>
                    <tr
                      key={`h-${bono}`}
                      className="cursor-pointer hover:bg-bg-elevated transition-colors border-b border-bg-border bg-bg-elevated/50"
                      onClick={() => toggle(bono)}
                    >
                      <td className="table-td w-8">
                        {open ? (
                          <ChevronDown size={14} className="text-gold-primary" />
                        ) : (
                          <ChevronRight size={14} className="text-text-secondary" />
                        )}
                      </td>
                      <td className="table-td">
                        <span className="font-medium text-white">{bono}</span>
                        <span className="text-text-secondary text-xs ml-2">
                          {cs.length} venta{cs.length > 1 ? 's' : ''}
                        </span>
                      </td>
                      <td className="table-td">
                        {cs[0] && (
                          <span className={TIPO_COLORS[cs[0].tipo] ?? 'badge-gold'}>
                            {TIPO_LABELS[cs[0].tipo] ?? cs[0].tipo}
                          </span>
                        )}
                      </td>
                      <td className="table-td" />
                      <td className="table-td text-right font-oswald text-gold-primary">
                        {(total / 100).toFixed(2)}€
                      </td>
                    </tr>
                    {open &&
                      cs.map((c) => (
                        <tr key={c.id} className="table-row border-b border-bg-border/50">
                          <td className="table-td" />
                          <td className="table-td pl-8 text-text-secondary text-sm">{c.alumno}</td>
                          <td className="table-td" />
                          <td className="table-td text-text-secondary text-xs">
                            {format(new Date(c.purchase_date), 'dd/MM/yyyy')}
                          </td>
                          <td className="table-td text-right text-text-secondary font-inter">
                            {(c.precio / 100).toFixed(2)}€
                          </td>
                        </tr>
                      ))}
                  </>
                );
              })}
            </tbody>
            <tfoot className="border-t-2 border-bg-border bg-bg-elevated">
              <tr>
                <td
                  colSpan={4}
                  className="table-td text-right font-oswald text-text-secondary uppercase tracking-wider text-xs"
                >
                  Total mes
                </td>
                <td className="table-td text-right font-oswald text-gold-primary text-lg">
                  {(totalMes / 100).toFixed(2)}€
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </>
  );
}

// ── Sub-componente: entrenadores ──────────────────────────────────────────────

function NotaMedia({ valor }: { valor: number | null }) {
  if (valor === null) return <span className="text-text-secondary text-xs">Sin valoraciones</span>;
  const stars = Math.round(valor);
  return (
    <span className="flex items-center gap-1">
      <span className="font-oswald text-gold-primary">{valor.toFixed(1)}</span>
      <span className="text-xs text-text-secondary">/ 5</span>
      <span className="text-gold-primary text-xs">
        {'★'.repeat(stars)}
        {'☆'.repeat(5 - stars)}
      </span>
    </span>
  );
}

function VistaEntrenadores({ sesiones, mes }: { sesiones: SesionEntrenador[]; mes: string }) {
  const byMonitor: Record<string, SesionEntrenador[]> = {};
  sesiones.forEach((s) => {
    if (!byMonitor[s.monitor]) byMonitor[s.monitor] = [];
    byMonitor[s.monitor].push(s);
  });
  const entries = Object.entries(byMonitor).sort((a, b) => a[0].localeCompare(b[0]));

  function exportCSV() {
    const lines = ['Entrenador,Clase,Tipo,Fecha,Hora,Alumnos,Nota media'];
    entries.forEach(([monitor, ss]) => {
      ss.forEach((s) => {
        lines.push(
          `"${monitor}","${s.grupo}","${TIPO_LABELS[s.tipo] ?? s.tipo}","${format(new Date(s.date), 'dd/MM/yyyy')}","${s.time_start.slice(0, 5)}","${s.alumnos}","${s.nota_media !== null ? s.nota_media.toFixed(1) : '—'}"`
        );
      });
    });
    dl(lines.join('\n'), `contabilidad_entrenadores_${mes}.csv`);
  }

  return (
    <>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-text-secondary text-sm">
          {sesiones.length} sesiones · {entries.length} entrenador{entries.length > 1 ? 'es' : ''}
        </div>
        <button onClick={exportCSV} className="btn-gold flex items-center gap-2">
          <Download size={16} /> Exportar CSV
        </button>
      </div>

      {entries.length === 0 ? (
        <div className="card text-text-secondary text-center py-12 text-sm">
          Sin sesiones en este mes
        </div>
      ) : (
        <div className="space-y-6">
          {entries.map(([monitor, ss]) => {
            const sorted = [...ss].sort((a, b) => a.date.localeCompare(b.date));
            const totalAlumnos = ss.reduce((a, s) => a + s.alumnos, 0);
            const notasValidas = ss
              .filter((s) => s.nota_media !== null)
              .map((s) => s.nota_media as number);
            const notaGlobal = notasValidas.length
              ? notasValidas.reduce((a, b) => a + b, 0) / notasValidas.length
              : null;
            return (
              <div key={monitor} className="card p-0 overflow-hidden">
                {/* Cabecera del entrenador */}
                <div className="flex items-center justify-between px-4 py-3 bg-bg-elevated border-b border-bg-border">
                  <div>
                    <span className="font-oswald text-white text-lg uppercase tracking-wide">
                      {monitor}
                    </span>
                    <span className="text-text-secondary text-xs ml-3">
                      {ss.length} sesión{ss.length > 1 ? 'es' : ''} · {totalAlumnos} alumnos total
                    </span>
                  </div>
                  {notaGlobal !== null && (
                    <div className="text-right">
                      <p className="text-text-secondary text-xs mb-0.5">Nota media global</p>
                      <NotaMedia valor={notaGlobal} />
                    </div>
                  )}
                </div>
                {/* Resumen por tipo */}
                <div className="flex gap-3 px-4 py-2.5 border-b border-bg-border bg-bg-elevated/30">
                  {(['grupal', 'individual', 'especial'] as const).map((t) => {
                    const count = ss.filter((s) => s.tipo === t).length;
                    return (
                      <div key={t} className="flex items-center gap-1.5">
                        <span className={TIPO_COLORS[t]}>{TIPO_LABELS[t]}</span>
                        <span className="font-oswald text-white text-sm">{count}</span>
                      </div>
                    );
                  })}
                </div>
                {/* Tabla de sesiones */}
                <table className="w-full">
                  <thead className="border-b border-bg-border">
                    <tr>
                      <th className="table-th">Clase</th>
                      <th className="table-th">Tipo</th>
                      <th className="table-th">Fecha</th>
                      <th className="table-th">Hora</th>
                      <th className="table-th text-right">Alumnos</th>
                      <th className="table-th">Nota media</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((s) => (
                      <tr key={s.id} className="table-row border-b border-bg-border/50">
                        <td className="table-td font-medium text-white">{s.grupo}</td>
                        <td className="table-td">
                          <span className={TIPO_COLORS[s.tipo] ?? 'badge-gold'}>
                            {TIPO_LABELS[s.tipo] ?? s.tipo}
                          </span>
                        </td>
                        <td className="table-td text-text-secondary text-xs">
                          {format(new Date(s.date), 'dd/MM/yyyy')}
                        </td>
                        <td className="table-td text-text-secondary text-xs">
                          {s.time_start.slice(0, 5)}
                        </td>
                        <td className="table-td text-right font-oswald text-gold-primary">
                          {s.alumnos}
                        </td>
                        <td className="table-td">
                          <NotaMedia valor={s.nota_media} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// ── Sub-componente: ingresos por concepto ────────────────────────────────────
// Las cuatro columnas que pidió dirección. A diferencia de las otras dos
// vistas, esta NO lee `user_credits`: el dinero sale de `orders` por la RPC de
// la mig 339. Las otras dos se quedan porque responden a otra pregunta —qué
// bonos tiene cada alumno—, pero el número que cuadra con el banco es este.
function VistaIngresos({
  filas,
  error,
  onRecargar,
}: {
  filas: FilaDeContabilidad[];
  error: string;
  onRecargar: () => void;
}) {
  // El mes se DERIVA, no se sincroniza con un efecto. `filas` llega vacío en el
  // primer render y con datos después; guardarlo en estado y corregirlo con un
  // `useEffect` dejaría un render intermedio con el mes equivocado. Así,
  // mientras lo elegido siga existiendo se respeta, y si no —o si nadie ha
  // elegido— se cae al mes más reciente con ventas, que es «el mes que toca».
  const meses = mesesConVentas(filas);
  const [mesElegido, setMesElegido] = useState<string | null>(null);
  const mes =
    mesElegido && (mesElegido === TODOS_LOS_MESES || meses.includes(mesElegido))
      ? mesElegido
      : (meses[0] ?? TODOS_LOS_MESES);

  const filasVisibles = filasDelMes(filas, mes);
  const total = filasVisibles.reduce((s, f) => s + f.importe_cents, 0);

  if (error) {
    return (
      <div className="card p-6 space-y-3" data-testid="ingresos-error">
        <p className="text-text-secondary text-sm">No se ha podido leer la contabilidad: {error}</p>
        <button onClick={onRecargar} className="btn-ghost text-sm">
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <label className="label mb-0" htmlFor="ingresos-mes">
            Mes:
          </label>
          {/* Solo los meses que TIENEN ventas. Un desplegable con doce meses de
              los que diez están vacíos obliga a probar para saber dónde hay
              algo. */}
          <select
            id="ingresos-mes"
            className="input w-48 h-9 text-sm"
            value={mes}
            onChange={(e) => setMesElegido(e.target.value)}
            data-testid="ingresos-mes"
            disabled={meses.length === 0}
          >
            {meses.map((m) => (
              <option key={m} value={m}>
                {etiquetaDeMes(m)}
              </option>
            ))}
            {meses.length > 1 && (
              <option value={TODOS_LOS_MESES}>{etiquetaDeMes(TODOS_LOS_MESES)}</option>
            )}
          </select>
          <span className="text-text-secondary text-sm" data-testid="ingresos-resumen">
            {filasVisibles.length} {filasVisibles.length === 1 ? 'fila' : 'filas'} · total{' '}
            <span className="text-white font-medium">{(total / 100).toFixed(2)} €</span>
          </span>
        </div>
        <button
          onClick={() => dl(csvDeContabilidad(filasVisibles), nombreDelCsv(mes))}
          className="btn-gold flex items-center gap-2"
          data-testid="ingresos-descargar"
          disabled={filasVisibles.length === 0}
        >
          <Download size={16} /> Descargar CSV
        </button>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-text-secondary border-b border-bg-border">
              <th className="text-left p-3 font-inter font-normal">Cliente</th>
              <th className="text-left p-3 font-inter font-normal">Fecha</th>
              <th className="text-left p-3 font-inter font-normal">Concepto</th>
              <th className="text-right p-3 font-inter font-normal">Importe total</th>
            </tr>
          </thead>
          <tbody>
            {filasVisibles.length === 0 ? (
              <tr>
                <td colSpan={4} className="p-6 text-center text-text-secondary">
                  No hay ingresos en el periodo.
                </td>
              </tr>
            ) : (
              filasVisibles.map((f, i) => (
                <tr
                  key={`${f.cliente}-${f.fecha}-${f.concepto}-${i}`}
                  className="border-b border-bg-border/50"
                >
                  <td className="p-3">{f.cliente}</td>
                  {/* Sin `new Date`: `new Date('2026-10-31')` es medianoche UTC
                      y en un huso negativo pinta el 30. La fecha ya viene como
                      `aaaa-mm-dd`, así que se le da la vuelta a la cadena. */}
                  <td className="p-3">{f.fecha.split('-').reverse().join('/')}</td>
                  <td className="p-3">{f.concepto}</td>
                  <td className="p-3 text-right">{(f.importe_cents / 100).toFixed(2)} €</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function dl(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Página principal ──────────────────────────────────────────────────────────

export default function ContabilidadPage() {
  // ⚠️ ARRANCA EN «ingresos», y no es cosmética. Jose entró después de
  // desplegarse el CSV nuevo y dijo que veía «lo mismo que antes»: tenía razón
  // para la pantalla que vio, porque esto arrancaba en «alumnos» y el informe
  // que cuadra con el banco estaba detrás del tercer botón. Las otras dos
  // pestañas responden a otra pregunta —qué bonos tiene cada alumno—, así que
  // la que se abre sola es la de contabilidad. Fijado en
  // `la-pestana-que-se-abre.test.ts`.
  const [tab, setTab] = useState<'alumnos' | 'entrenadores' | 'ingresos'>('ingresos');
  const [ingresos, setIngresos] = useState<FilaDeContabilidad[]>([]);
  const [errorIngresos, setErrorIngresos] = useState('');
  const [vistaAlumnos, setVistaAlumnos] = useState<'alumno' | 'bono'>('alumno');
  const [compras, setCompras] = useState<CompraAlumno[]>([]);
  const [sesiones, setSesiones] = useState<SesionEntrenador[]>([]);
  const [loading, setLoading] = useState(true);
  // [BUG 9] Esta página no tenía dónde decir que una lectura no entró, así que
  // un 300 se veía igual que un mes sin ventas.
  const [listError, setListError] = useState('');
  // `loadCompras` y `loadSesiones` corren en paralelo y la primera hace
  // `setListError('')` al terminar bien: compartir el hueco le borraría el
  // aviso a la otra. Cada una tiene el suyo.
  const [errorSesiones, setErrorSesiones] = useState('');
  // Las cuatro lecturas que adornan la sesión (notas, grupos, entrenadores,
  // clases especiales). Sin ellas la sesión sigue estando, pero el nombre del
  // grupo, el del entrenador y la media caen a «—» y a 0.
  const [fallidosSesiones, setFallidosSesiones] = useState<string[]>([]);
  const [mesAlumnos, setMesAlumnos] = useState(format(new Date(), 'yyyy-MM'));
  const [mesEntrenadores, setMesEntrenadores] = useState(format(new Date(), 'yyyy-MM'));

  useEffect(() => {
    loadAll();
    // `loadAll` se redefine en cada render (no está memoizado) y a su vez llama
    // a `loadCompras`/`loadSesiones`, también redefinidas. Listarlo aquí entra
    // en bucle de recarga infinito (efecto → setState → render → nueva
    // referencia de `loadAll` → efecto). El bootstrap "1 vez al montar" es
    // intencional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadAll() {
    setLoading(true);
    await Promise.all([loadCompras(), loadSesiones(), loadIngresos()]);
    setLoading(false);
  }

  async function loadCompras() {
    // [BUG 9] FK NOMBRADA. `user_credits` tiene dos claves ajenas a `profiles`
    // —`user_id` y `granted_by` (quién lo regaló)—: con `profiles(...)` a secas
    // PostgREST responde 300 PGRST201 y no devuelve ninguna compra. Aquí manda
    // `user_id`, que es el alumno de la compra.
    const { data, error } = await supabase
      .from('user_credits')
      .select(
        'id, purchase_date, sessions_total, sessions_remaining, credit_type, profiles!user_id(full_name), session_packs(name, price_cents)'
      )
      .order('purchase_date', { ascending: false });
    // Una lectura que falla no es "no hay compras": se dice, y no se pinta una
    // tabla vacía que parece un dato.
    if (error) {
      setListError(`No se pudieron cargar las compras: ${error.message}`);
      setCompras([]);
      return;
    }
    setListError('');
    // supabase-js infiere los embeds como arrays cuando no recibe el genérico
    // `<Database>` (no sabe la dirección de la FK); en runtime PostgREST devuelve
    // un objeto para relaciones many-to-one. Doble cast vía `unknown` para no
    // reintroducir `any`.
    const rows = (data ?? []) as unknown as CompraRow[];
    setCompras(
      rows.map((c) => ({
        id: c.id,
        purchase_date: c.purchase_date,
        sessions_total: c.sessions_total,
        sessions_remaining: c.sessions_remaining,
        alumno: c.profiles?.full_name ?? '—',
        bono: c.session_packs?.name ?? '—',
        precio: c.session_packs?.price_cents ?? 0,
        tipo: c.credit_type ?? 'grupal',
      }))
    );
  }

  async function loadIngresos() {
    const r = await leerContabilidadPorConcepto();
    if (r.estado === 'error') {
      setErrorIngresos(r.mensaje);
      setIngresos([]);
      return;
    }
    setErrorIngresos('');
    setIngresos(r.filas);
  }

  async function loadSesiones() {
    const noLeidos: string[] = [];
    // Por `leerVarias`: el error de las notas se caía en el destructuring
    // —`{ data: ratingsData }`— y el de las sesiones solo iba a
    // `console.error`, que no lo ve nadie mirando la pantalla.
    const { datos, fallidos } = await leerVarias([
      {
        que: QUE_SESIONES,
        consulta: supabase
          .from('sessions')
          .select(
            'id, date, time_start, current_count, status, is_special_class, group_id, special_class_id'
          )
          .neq('status', 'cancelled')
          .order('date', { ascending: false }),
      },
      { que: QUE_NOTAS, consulta: supabase.from('session_ratings').select('session_id, rating') },
    ]);
    const [rawSessions, ratingsData] = datos;

    // Sin las sesiones no hay nada que liquidar, y la pestaña lo remata con
    // «Sin sesiones en este mes»: un mes de trabajo del entrenador que no se
    // paga porque una lectura no entró.
    if (fallidos.includes(QUE_SESIONES)) {
      setErrorSesiones(
        'No se han podido cargar las sesiones. No es un mes sin sesiones: la consulta ha fallado.'
      );
      setSesiones([]);
      setFallidosSesiones([]);
      return;
    }
    setErrorSesiones('');
    if (fallidos.includes(QUE_NOTAS)) noLeidos.push(QUE_NOTAS);

    const sessionsList = (rawSessions ?? []) as SessionListRow[];
    const groupIds = [
      ...new Set(sessionsList.filter((s) => s.group_id).map((s) => s.group_id as string)),
    ];
    const scIds = [
      ...new Set(
        sessionsList.filter((s) => s.special_class_id).map((s) => s.special_class_id as string)
      ),
    ];

    // Fetch groups
    let groupMap: Record<string, GroupWithMonitor> = {};
    if (groupIds.length) {
      const { data: groupsRaw, error: errorGrupos } = await supabase
        .from('groups')
        .select('id, name, class_type, monitor_id')
        .in('id', groupIds);
      if (errorGrupos) noLeidos.push(QUE_GRUPOS);
      const groupsList = (groupsRaw ?? []) as GroupListRow[];
      const monitorIds = [
        ...new Set(groupsList.filter((g) => g.monitor_id).map((g) => g.monitor_id as string)),
      ];
      let monitorMap: Record<string, string> = {};
      if (monitorIds.length) {
        const { data: monitorsRaw, error: errorMonitores } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', monitorIds);
        if (errorMonitores) noLeidos.push(QUE_ENTRENADORES);
        const monitorsList = (monitorsRaw ?? []) as MonitorListRow[];
        monitorMap = Object.fromEntries(monitorsList.map((p) => [p.id, p.full_name]));
      }
      groupMap = Object.fromEntries(
        groupsList.map((g) => [
          g.id,
          { ...g, monitor_name: (g.monitor_id && monitorMap[g.monitor_id]) ?? '—' },
        ])
      );
    }

    // Fetch special class names
    let scMap: Record<string, string> = {};
    if (scIds.length) {
      const { data: scRaw, error: errorEspeciales } = await supabase
        .from('special_classes')
        .select('id, name')
        .in('id', scIds);
      if (errorEspeciales) noLeidos.push(QUE_ESPECIALES);
      const scList = (scRaw ?? []) as SpecialClassListRow[];
      scMap = Object.fromEntries(scList.map((sc) => [sc.id, sc.name]));
    }

    // Ratings lookup
    const ratingsBySession: Record<string, number[]> = {};
    const ratingsList = (ratingsData ?? []) as RatingListRow[];
    ratingsList.forEach((r) => {
      if (!ratingsBySession[r.session_id]) ratingsBySession[r.session_id] = [];
      ratingsBySession[r.session_id].push(r.rating);
    });

    setSesiones(
      sessionsList.map((s) => {
        const group = s.group_id ? groupMap[s.group_id] : null;
        const ratings = ratingsBySession[s.id];
        const nota_media = ratings?.length
          ? ratings.reduce((a, b) => a + b, 0) / ratings.length
          : null;
        return {
          id: s.id,
          date: s.date,
          time_start: s.time_start,
          monitor: group?.monitor_name ?? '—',
          grupo: s.is_special_class
            ? s.special_class_id
              ? (scMap[s.special_class_id] ?? 'Clase especial')
              : 'Clase especial'
            : (group?.name ?? '—'),
          tipo: s.is_special_class ? 'especial' : (group?.class_type ?? 'grupal'),
          alumnos: s.current_count ?? 0,
          status: s.status,
          nota_media,
        };
      })
    );
    setFallidosSesiones(noLeidos);
  }

  const comprasFiltradas = compras.filter((c) => c.purchase_date?.startsWith(mesAlumnos));
  const sesionesFiltradas = sesiones.filter((s) => s.date?.startsWith(mesEntrenadores));

  return (
    <div className="space-y-6">
      {listError && (
        <p
          className="text-error text-sm border border-error/30 rounded px-3 py-2"
          data-testid="contabilidad-list-error"
        >
          {listError}
        </p>
      )}

      {errorSesiones && (
        <p
          className="text-error text-sm border border-error/30 rounded px-3 py-2"
          data-testid="contabilidad-sesiones-error"
        >
          {errorSesiones}
        </p>
      )}

      {fallidosSesiones.length > 0 && (
        <p
          className="text-error text-sm border border-error/30 rounded px-3 py-2"
          data-testid="contabilidad-sesiones-fallidos"
        >
          No se ha podido leer {listaEnCastellano(fallidosSesiones)}. Las sesiones y su ocupación
          están; el nombre del grupo, el del entrenador y la media de valoración, no.
        </p>
      )}

      {/* Tabs + Refresh */}
      <div className="flex items-center justify-between">
        <div className="flex gap-3">
          <button
            onClick={() => setTab('alumnos')}
            className={tab === 'alumnos' ? 'btn-gold' : 'btn-ghost'}
          >
            Alumnos
          </button>
          <button
            onClick={() => setTab('entrenadores')}
            className={tab === 'entrenadores' ? 'btn-gold' : 'btn-ghost'}
          >
            Entrenadores
          </button>
          <button
            onClick={() => setTab('ingresos')}
            className={tab === 'ingresos' ? 'btn-gold' : 'btn-ghost'}
          >
            Ingresos
          </button>
        </div>
        <button onClick={loadAll} className="btn-ghost flex items-center gap-2 text-sm">
          <RefreshCw size={14} /> Actualizar
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
        </div>
      ) : tab === 'ingresos' ? (
        <VistaIngresos filas={ingresos} error={errorIngresos} onRecargar={loadIngresos} />
      ) : tab === 'alumnos' ? (
        <>
          {/* Filtros: mes + vista */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <label className="label mb-0">Mes:</label>
              <input
                type="month"
                className="input w-44 h-9 text-sm"
                value={mesAlumnos}
                onChange={(e) => setMesAlumnos(e.target.value)}
              />
            </div>
            <div className="flex rounded-lg border border-bg-border overflow-hidden">
              <button
                onClick={() => setVistaAlumnos('alumno')}
                className={`px-4 py-1.5 text-sm font-inter transition-colors ${vistaAlumnos === 'alumno' ? 'bg-gold-primary text-bg-primary font-medium' : 'text-text-secondary hover:text-white hover:bg-bg-elevated'}`}
              >
                Por alumno
              </button>
              <button
                onClick={() => setVistaAlumnos('bono')}
                className={`px-4 py-1.5 text-sm font-inter transition-colors ${vistaAlumnos === 'bono' ? 'bg-gold-primary text-bg-primary font-medium' : 'text-text-secondary hover:text-white hover:bg-bg-elevated'}`}
              >
                Por tipo de bono
              </button>
            </div>
          </div>

          {vistaAlumnos === 'alumno' ? (
            <VistaAlumno compras={comprasFiltradas} mes={mesAlumnos} />
          ) : (
            <VistaBono compras={comprasFiltradas} mes={mesAlumnos} />
          )}
        </>
      ) : (
        <>
          {/* Filtro mes entrenadores */}
          <div className="flex items-center gap-3">
            <label className="label mb-0">Mes:</label>
            <input
              type="month"
              className="input w-44 h-9 text-sm"
              value={mesEntrenadores}
              onChange={(e) => setMesEntrenadores(e.target.value)}
            />
          </div>

          <VistaEntrenadores sesiones={sesionesFiltradas} mes={mesEntrenadores} />
        </>
      )}
    </div>
  );
}
